<template>
  <div>
    <form v-if="!stepOneComplete" :class="$style.container">
      <p :class="$style.text">
        {{ config.text }}
      </p>
      <div class="columns">
        <div class="column is-half-tablet">
          <FormInput
            v-model.trim="v$.formData.email.$model"
            :bold-label="true"
            :error="v$.formData.email.$error && v$.$dirty && isStepOneSubmitted"
            :error-message="config.labels.email.errorMessage"
            :form-submit-switch="formSubmitSwitch"
            :label="config.labels.email.label"
            :original-value="previousEmail && previousEmail !== '' ? previousEmail : null"
          />
        </div>
      </div>
      <div v-if="serverError" :class="$style.serverError">
        {{ config.serverErrorMessage }}
      </div>
      <div v-if="noSubscriptionsError" :class="$style.serverError">
        {{ config.noSubscriptionsErrorMessage }}
      </div>
      <div class="columns">
        <div class="column has-text-right-tablet">
          <Button
            colour="blue"
            :is-form="true"
            :text="config.firstButtonText"
            type="submit"
            @close="handleStepOneSubmit"
          />
        </div>
      </div>
      <ReCaptcha :execute-switch="executeSwitch" :submit="stepOneSubmit" />
    </form>
    <form v-else :class="$style.container">
      <p :class="$style.stepTwoTitle">
        {{ config.optionalSectionTitle }}
      </p>
      <p :class="$style.text">
        <span v-if="checkboxes.length > 1">{{ config.optionalSectionTextPartOne }}</span>
        <span>{{ config.optionalSectionTextPartTwo }}</span>
      </p>
      <div :class="[$style.checkboxes, 'columns is-mobile is-multiline']">
        <div
          v-for="(checkbox, index) in checkboxes"
          :key="`checkbox-${index}`"
          class="column is-half"
        >
          <input
            :id="`checkbox-${index}`"
            v-model="selectedIds"
            :name="`checkbox-${index}`"
            type="checkbox"
            :value="checkbox.newsletterId"
          />
          <label :for="`checkbox-${index}`">{{ checkbox.label }}</label>
        </div>
      </div>
      <div v-if="serverError" :class="$style.serverError">
        {{ config.serverErrorMessage }}
      </div>
      <div v-if="serverSuccess" :class="$style.serverSuccess">
        {{ config.unsubscribeSuccess }}
      </div>
      <div v-if="checkboxError" :class="$style.serverError">
        {{ config.checkboxErrorMessage }}
      </div>
      <div class="columns">
        <div class="column has-text-right-tablet">
          <Button
            colour="blue"
            :is-form="true"
            :text="config.secondButtonText"
            type="submit"
            @close="handleStepTwoSubmit"
          />
        </div>
      </div>
      <ReCaptcha :execute-switch="executeSwitch" :submit="stepTwoSubmit" />
    </form>
  </div>
</template>

<script setup>
import { required, email as emailRule } from '@vuelidate/validators';
import ReCaptcha from '../ReCaptcha/ReCaptcha.vue';
import FormInput from '../FormInput/FormInput.vue';
import Button from '../Button/Button.vue';
import { useVuelidate } from '@vuelidate/core';
import { onMounted, ref } from 'vue';
import safeFetch from '../../utils/safeFetch';

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
});

// form refs
const formData = ref({
  newsletter: '',
  email: '',
  domain: '',
  pageUrl: '',
  token: '',
});

// regular refs
const checkboxes = ref([]);
const newsletterIds = ref([]);
const selectedIds = ref([]);
const previousEmail = ref('');
const isStepOneSubmitted = ref(false);
const formSubmitSwitch = ref(false);
const executeSwitch = ref(false);
const stepOneComplete = ref(false);
const serverError = ref(false);
const serverSuccess = ref(false);
const noSubscriptionsError = ref(false);
const checkboxError = ref(false);

const rules = {
  formData: {
    email: {
      required,
      emailRule,
    },
  },
};

const v$ = useVuelidate(rules, { formData });

onMounted(() => {
  if (props.config.newsletters) {
    for (const newsletter of props.config.newsletters) {
      newsletterIds.value.push(parseFloat(newsletter.newsletterId));
    }
  }
});

const handleStepOneSubmit = () => {
  serverError.value = false;
  serverSuccess.value = false;
  v$.value.$touch();
  isStepOneSubmitted.value = true;
  formSubmitSwitch.value = !formSubmitSwitch.value;
  if (!v$.$invalid) {
    executeSwitch.value = !executeSwitch.value;
  } else {
    previousEmail.value = formData.value.email;
  }
};

const initStepTwo = (data) => {
  checkboxes.value = [];
  for (const id of data) {
    const item = {
      newsletterId: id.toString(),
      label: props.config.newsletters[newsletterIds.value.indexOf(id)].label,
    };
    checkboxes.value.push(item);
  }
  stepOneComplete.value = true;
};

const handleStepTwoSubmit = () => {
  serverError.value = false;
  serverSuccess.value = false;
  noSubscriptionsError.value = false;
  checkboxError.value = false;
  v$.value.formData.email.$touch();
  if (!v$.value.$invalid) {
    if (selectedIds.value.length !== 0) {
      executeSwitch.value = !executeSwitch.value;
    } else {
      checkboxError.value = true;
    }
  } else {
    previousEmail.value = formData.value.email;
  }
};

const stepOneSubmit = (newToken) => {
  formData.value.token = newToken;
  formData.value.domain = window.location.origin;
  formData.value.pageUrl = window.location.pathname;
  const params = new URLSearchParams({
    email: formData.value.email,
    listIds: newsletterIds.value.toString(),
  });
  safeFetch(`${window.newsletterGetSubscriptionsUrl}?${params}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      if (data.length !== 0) {
        initStepTwo(data);
      } else {
        serverError.value = true;
      }
    })
    .catch((error) => {
      serverError.value = true;
      console.error(error);
    });
};

const stepTwoSubmit = (token) => {
  formData.value.token = token;
  formData.value.domain = window.location.origin;
  formData.value.pageUrl = window.location.pathname;
  let combinedRequests = [];
  for (const id of selectedIds.value) {
    formData.value.newsletter = id;
    combinedRequests.push(
      safeFetch(window.newsletterUnsubscribeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData.value }),
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response;
      }),
    );
  }
  Promise.all(combinedRequests)
    .then((responses) => {
      if (responses.every((response) => response.status === 200)) {
        serverSuccess.value = true;
      } else {
        serverError.value = true;
        serverSuccess.value = false;
      }
    })
    .catch((errors) => {
      serverError.value = true;
      serverSuccess.value = false;
      console.error(errors);
    });
};
</script>

<style module lang="scss">
@forward './NewsletterForm.scss';
</style>
